import React, { useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import ProductCard from './ProductCard';

const GET_CATEGORIES = gql`
    query getMegaMenu {
        categoryList {
            id
            uid
            name
            children {
                id
                uid
                include_in_menu
                name
                position
                url_path
                url_suffix
                children {
                    id
                    uid
                    include_in_menu
                    name
                    position
                    url_path
                    url_suffix
                    children {
                        id
                        uid
                        include_in_menu
                        name
                        position
                        url_path
                        url_suffix
                    }
                }
            }
        }
    }
`;

const GET_FEATURED_PRODUCTS = gql`
    query getFeaturedProducts {
        products(filter: {}, currentPage: 1, pageSize: 8) {
            items {
                id
                uid
                sku
                name
                small_image {
                    url
                }
                url_key
                url_suffix
                price_range {
                    minimum_price {
                        final_price {
                            currency
                            value
                        }
                    }
                }
                rating_summary
                review_count
                stock_status
            }
        }
    }
`;

const Home = () => {
  const { loading: categoriesLoading, error: categoriesError, data: categoriesData } = useQuery(GET_CATEGORIES);
  const { loading: productsLoading, error: productsError, data: productsData } = useQuery(GET_FEATURED_PRODUCTS);
  const { updateBreadcrumbs } = useBreadcrumb();

  // Clear breadcrumbs on home page
  useEffect(() => {
    updateBreadcrumbs([]);
  }, [updateBreadcrumbs]);

  // Get categories from root level
  const rootCategories = categoriesData?.categoryList || [];

  // Get main menu categories (first level children that are in menu)
  const mainCategories = rootCategories
    .flatMap(root => root.children || [])
    .filter(cat => cat.include_in_menu)
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  const products = productsData?.products?.items || [];

  // Helper function to render category cards
  const renderCategoryCard = (category, isSubCategory = false, parentPath = '') => {
    return (
      <Link
        key={category.uid}
        to={`/${category.url_path}${category.url_suffix || ''}`}
        className="product-card"
        style={{ textDecoration: 'none' }}
      >
        <div style={{
          height: '200px',
          backgroundColor: isSubCategory ? '#e5e7eb' : '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: isSubCategory ? '2rem' : '3rem'
        }}>
          {isSubCategory ? '📁' : '📂'}
        </div>
        <div className="product-info">
          <h3 className="product-title">{category.name}</h3>
          {parentPath && (
            <p style={{ color: '#7c3aed', fontSize: '0.75rem' }}>{parentPath}</p>
          )}
        </div>
      </Link>
    );
  };

  // Collect all subcategories for display
  const getAllSubCategories = (categories) => {
    const allSubs = [];
    categories.forEach(mainCat => {
      if (mainCat.children) {
        mainCat.children.forEach(subCat => {
          if (subCat.include_in_menu) {
            allSubs.push({
              ...subCat,
              parentPath: mainCat.name
            });

            // Add third level categories
            if (subCat.children) {
              subCat.children.forEach(subSubCat => {
                if (subSubCat.include_in_menu) {
                  allSubs.push({
                    ...subSubCat,
                    parentPath: `${mainCat.name} > ${subCat.name}`
                  });
                }
              });
            }
          }
        });
      }
    });
    return allSubs.sort((a, b) => (a.position || 0) - (b.position || 0));
  };

  const allSubCategories = getAllSubCategories(mainCategories);

  return (
    <div>
      {/* Hero Banner - Static version already loaded, keep it visible */}
      <section style={{
        marginBottom: '3rem'
      }}>
        <img
          src="/hero-banner.svg"
          alt="Amazing Deals - Up to 70% OFF"
          style={{
            width: '100%',
            height: '600px',
            objectFit: 'cover',
            display: 'block'
          }}
        />
      </section>

      {/* Featured Products Section */}
      <section>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Featured Products</h2>
        {productsLoading ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '1.5rem'
          }}>
            {[...Array(8)].map((_, i) => (
              <div key={i} style={{
                height: '350px',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
                animation: 'pulse 2s infinite'
              }} />
            ))}
          </div>
        ) : productsError ? (
          <div>Products Error: {productsError.message}</div>
        ) : (
          <div className="product-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;